<script setup lang="ts">
import DeleteEntityDialog from '@/common/components/DeleteEntityDialog.vue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Card, Column, DataTable, InputText, type DataTableRowDoubleClickEvent } from 'primevue';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useProductStore } from 'shared/products/stores/productStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useMappingProfileStore } from 'shared/products/mapping/stores/mappingProfileStore';
import { useRefreshMappingProfileStore } from '../commands/refreshMappingProfileStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshPackageTypeStore } from '@/packages/commands/refreshPackageTypeStore';
import { useRefreshPackagingStore } from '@/packages/commands/refreshPackagingStore';
import EditMappingProfileDialog from './EditMappingProfileDialog.vue';
import { useDeleteMappingProfile } from '../api/deleteMappingProfile';

const selectedColumnsStorageKey = 'mapping-profiles-table-columns';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useMappingProfileStore();
const refreshMappingProfileStore = useRefreshMappingProfileStore();
const refreshProductStore = useRefereshProductStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshPackagingStore = useRefreshPackagingStore();
const productStore = useProductStore();
const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();
const deleteMappingProfile = useDeleteMappingProfile();

const selectedRow = ref<{ id: string } | null>(null);
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');

const columnDefinitions = computed<ColumnField[]>(() => [
  { field: 'name', header: translations.value.generic.name, filterType: 'text' },
  { field: 'priority', header: translations.value.generic.priority, filterType: 'numeric' },
  { field: 'criteria', header: translations.value.products.mapping.matchCriteria, filterType: 'text' },
  { field: 'target', header: translations.value.products.mapping.target, filterType: 'text' },
]);

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({ storageKey: selectedColumnsStorageKey, columns: columnDefinitions });

function labelForProduct(id: string | null): string | null {
  if (!id) return null;
  return productStore.getById(id)?.name ?? id;
}

function labelForPackageType(id: string | null): string | null {
  if (!id) return null;
  return packageTypeStore.getById(id)?.name ?? id;
}

function labelForPackaging(id: string | null): string | null {
  if (!id) return null;
  return packagingStore.getById(id)?.name ?? id;
}

const rows = computed(() => {
  return store.sortedByPriority.map((item) => ({
    id: item.id,
    name: item.name,
    priority: item.priority,
    criteria: [
      item.sourceCode ? `Code: ${item.sourceCode}` : null,
      item.minWidth !== null || item.maxWidth !== null ? `W: ${item.minWidth ?? '-'}..${item.maxWidth ?? '-'}` : null,
      item.minHeight !== null || item.maxHeight !== null ? `H: ${item.minHeight ?? '-'}..${item.maxHeight ?? '-'}` : null,
      item.minLength !== null || item.maxLength !== null ? `L: ${item.minLength ?? '-'}..${item.maxLength ?? '-'}` : null,
      item.minMoisture !== null || item.maxMoisture !== null ? `M: ${item.minMoisture ?? '-'}..${item.maxMoisture ?? '-'}` : null,
    ].filter((part): part is string => !!part).join(' | '),
    target: [
      item.targetProductId ? `Product: ${labelForProduct(item.targetProductId)}` : null,
      item.targetPackageTypeId ? `Package type: ${labelForPackageType(item.targetPackageTypeId)}` : null,
      item.targetPackagingId ? `Packaging: ${labelForPackaging(item.targetPackagingId)}` : null,
    ].filter((part): part is string => !!part).join(' | '),
  }));
});

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => rows.value.length,
});

const selected = computed(() => selectedRow.value?.id);

clearCorruptDataTableState('mapping-profiles-table-state');

onMounted(() => {
  refreshProductStore.initialize();
  refreshPackageTypeStore.initialize();
  refreshPackagingStore.initialize();
  refreshMappingProfileStore.initialize();
});

const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

function onAdd() {
  editDialog.value?.show();
}

function onEdit() {
  if (!selected.value) return;
  editDialog.value?.show(selected.value);
}

function onCopy() {
  if (!selected.value) return;
  editDialog.value?.show(selected.value, 'copy');
}

function onDelete() {
  if (!selected.value) return;
  deleteDialog.value?.show(selected.value);
}

function onRowDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const mappingProfile = ev.data as { id: string };
  selectedRow.value = { id: mappingProfile.id };
  editDialog.value?.show(mappingProfile.id);
}

function onSaved(id: string) {
  selectedRow.value = { id };
}

function onDeleted(id: string) {
  if (selectedRow.value?.id === id) {
    selectedRow.value = null;
  }
}

function getMappingProfileForDelete(id: string) {
  return Promise.resolve({
    id,
    name: store.getById(id)?.name ?? id,
  });
}
</script>

<template>
  <Card>
    <template #title>
      <div class="title-row">
        <span>Mapping profile</span>
        <div class="actions">
          <span v-if="hasActiveFilters && filteredCount < rows.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ rows.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
          <Button :label="translations.generic.add" @click="onAdd"></Button>
          <Button :label="translations.generic.edit" outlined :disabled="!selected" @click="onEdit"></Button>
          <Button :label="translations.generic.copy" outlined :disabled="!selected" @click="onCopy"></Button>
          <Button :label="translations.generic.delete" outlined :disabled="!selected" @click="onDelete"></Button>
          <InputText v-model="filters.global.value" :placeholder="translations.generic.search"></InputText>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:selection="selectedRow"
        v-model:filters="filters"
        v-model:multiSortMeta="multiSortMeta"
        :class="sortBadgeClass"
        :value="rows"
        data-key="id"
        selectionMode="single"
        sort-mode="multiple"
        stateStorage="local"
        stateKey="mapping-profiles-table-state"
        filterDisplay="menu"
        @filter="onFilter"
        @row-dblclick="onRowDoubleClick"
      >
        <Column v-for="(col, index) of selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column column-key="column-selector" class="w-12 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditMappingProfileDialog ref="edit-dialog" @saved="onSaved"></EditMappingProfileDialog>
  <DeleteEntityDialog
    ref="delete-dialog"
    :delete="(id) => deleteMappingProfile.execute({ id })"
    :getById="getMappingProfileForDelete"
    :translate="(t) => t.products.deleteDialog"
    @deleted="onDeleted"
  ></DeleteEntityDialog>
</template>

<style scoped>
.title-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
